/* /sales/orders/:id — the order detail page (ORD-4). A full-page route now; the same
   `OrderDetail` will later render inside a drawer over the list (ORD-3) — this file owns only the
   id, the request and the loading/error/not-found states.

   `id` is a UUID string from Excalibur, never coerced to a number. */

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Alert as AntAlert, Button, Empty, Flex, Skeleton, Typography } from "antd";

import CounterpartySection from "../../component/CounterpartySection";
import { ORDERS_PAGE } from "../../config/lang";
import { call, isOk } from "../../util/action";
import { translate } from "../../util/util";

import GeoSection from "./GeoSection";
import OrderDetail, { OrderDetailHead } from "./OrderDetail";

const Page = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector(state => state.me?.language || "en");
  const t = key => translate(ORDERS_PAGE, key, lang);

  const [item, setItem] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error | not_found

  /* `isOk()` alone cannot tell a 404 from success here: `call()` forwards a 404's parsed JSON body
     straight to the callback with no HTTP status attached (that only survives on the raw 400/204
     Response objects `call()` special-cases), so a DRF `{"detail": "Not found."}` body reads as
     `isOk() === true`. It still has no `id`, which is what actually marks "no such record" —
     `isOk()` catches the network/5xx case (`failedResponse()`), the `id` check catches the 404. */
  const load = useCallback(() => {
    setStatus("loading");
    call({ method: "GET", url: `excalibur/orders/${id}/` }, response => {
      if (!isOk(response)) return setStatus("error");
      if (!response?.id) return setStatus("not_found");
      setItem(response);
      setStatus("ready");
    });
  }, [id]);

  useEffect(load, [load]);

  if (status === "loading") {
    return (
      <Flex vertical gap="middle" className="shell-content">
        <Skeleton active paragraph={{ rows: 8 }} />
      </Flex>
    );
  }

  if (status === "error") {
    return (
      <Flex vertical gap="middle" className="shell-content">
        <AntAlert
          type="error"
          showIcon
          message={t("error_title")}
          action={
            <Button size="small" onClick={load}>
              {t("retry")}
            </Button>
          }
        />
      </Flex>
    );
  }

  if (status === "not_found" || !item) {
    return (
      <Flex vertical gap="middle" className="shell-content">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={4} align="center">
              <Typography.Text strong>{t("not_found_title")}</Typography.Text>
              <Typography.Text type="secondary">{t("not_found_desc")}</Typography.Text>
            </Flex>
          }>
          {/* Carries the list's own query string back — `List.js` put it here when it navigated in
              (row menu / drawer "Open page"), so the filtered view this order was found from is
              what "До списку" restores, not a blank one (same bug `visit/View.js`'s breadcrumb
              had, per CLAUDE.md). A page opened from elsewhere — the counterparty card's Orders tab
              — names its own way back in `state.from` (an address object carrying the card's tab
              and its own route state), which wins. */}
          <Button onClick={() => history.push(location.state?.from || { pathname: "/sales/orders", search: location.search })}>{t("back_to_list")}</Button>
        </Empty>
      </Flex>
    );
  }

  return (
    <div className="shell-content">
      <OrderDetailHead item={item} lang={lang} />
      {/* Archetype B's form+rail grid (`detail-page.scss`, shared — imported once from
          `src/index.js`, no new import needed here). `CounterpartySection` covers name/address/
          phone/remaining-funds and the "all orders" link only (debt was a sixth row until the
          detail endpoints stopped sending `current_debt`) — the ТЗ's payment terms and
          last-90-days order count have no backing field yet and are a separate, later change.
          `dp-grid--fluid`: the main column grows with the viewport instead of sitting fixed at
          720px — a wide monitor otherwise left a few hundred idle pixels to the right of the
          rail, reading as an unfinished page. Opt-in modifier (`detail-page.scss`), so `/points`,
          `/import` and `/task` keep the fixed column they were built for. */}
      <div className="dp-grid dp-grid--fluid">
        <div className="dp-main">
          <OrderDetail item={item} lang={lang} showHeader={false} />
        </div>
        <aside className="dp-rail">
          <GeoSection item={item} lang={lang} />
          {/* Shared with `/sales/returns/:id` — see `component/CounterpartySection.js` for why,
              and for the lang keys this group has to carry. */}
          <CounterpartySection item={item} lang={lang} group={ORDERS_PAGE} actionKey="counterparty_all_orders_btn" listPath="/sales/orders" />
        </aside>
      </div>
    </div>
  );
};

export default Page;
